import React, { lazy, Suspense, useCallback, useMemo } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useIsHomeDisplayReady } from "@/modules/homeDisplay/hooks";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import { useUserDataFlattened } from "@/modules/user/stores";
import { ROUTES } from "@/types/routes";
import { markCashbackBoostRead } from "@/api/cashbackBoost";
import { useNavigation } from "@/utils/navigation";
import type { CashbackBoostPopupItem } from "./types";

const CashbackBoostCongratsDialog = lazy(() =>
  import("./CashbackBoostCongratsDialog").then(m => ({
    default: m.CashbackBoostCongratsDialog,
  }))
);

const POPUP_CFG = POPUP_CONFIGS.CASHBACK_BOOST_CONGRATS;

function pickNextPopup(
  list: CashbackBoostPopupItem[] | undefined
): CashbackBoostPopupItem | null {
  if (!list?.length) return null;
  const now = Date.now();
  const valid = list.filter(p => (p.expireAt ?? 0) > now);
  if (!valid.length) return null;
  return [...valid].sort((a, b) => a.expireAt - b.expireAt)[0];
}

const HomeCashbackBoostPopupHost: React.FC<{
  popup: CashbackBoostPopupItem | null;
  onComplete: () => void;
  onRefresh: () => Promise<void>;
}> = ({ popup, onComplete, onRefresh }) => {
  const { navigate } = useNavigation();

  const handleClose = async () => {
    if (popup?.recordId) {
      try {
        await markCashbackBoostRead(popup.recordId);
      } catch {
        /* ignore */
      }
    }
    await onRefresh();
    onComplete();
  };

  const handleClaim = async () => {
    if (popup?.recordId) {
      try {
        await markCashbackBoostRead(popup.recordId);
      } catch {
        /* ignore */
      }
    }
    await onRefresh();
    onComplete();
    navigate(`${ROUTES.VIP}?tabIndex=1`);
  };

  return (
    <CashbackBoostCongratsDialog
      visible={!!popup}
      record={popup}
      onClose={handleClose}
      onClaim={handleClaim}
    />
  );
};

export const useCashbackBoostPopup = () => {
  const isConfigReady = useIsHomeDisplayReady();
  const {
    isAuthenticated,
    data: userInfo,
    loadUserInfo,
  } = useUserDataFlattened();
  const popups = useHomeDisplayStore(s => s.data?.cashbackBoostPopups);
  const refreshConfig = useHomeDisplayStore(s => s.refreshConfig);

  const nextPopup = useMemo(() => pickNextPopup(popups), [popups]);

  const shouldShowFn = useCallback(() => {
    if (!isConfigReady || !isAuthenticated) return false;
    return nextPopup != null;
  }, [isConfigReady, isAuthenticated, nextPopup]);

  useQueuedPopup({
    popupId: POPUP_CFG.id,
    priority: POPUP_CFG.priority,
    shouldShow: shouldShowFn,
    renderContent: onComplete => (
      <Suspense fallback={null}>
        <HomeCashbackBoostPopupHost
          popup={nextPopup}
          onComplete={onComplete}
          onRefresh={async () => {
            try {
              await refreshConfig();
              await loadUserInfo();
            } catch {
              /* ignore */
            }
          }}
        />
      </Suspense>
    ),
    showOnce: false,
    allowManualTrigger: false,
    autoTrigger: true,
    dependencies: [isConfigReady, isAuthenticated, nextPopup?.recordId],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    blockedPaths: [
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId as string | undefined,
  });
};
